import PropTypes from 'prop-types';
import {useCallback, useState} from 'react';
import {FileUpload} from '@/components/atoms';
import UploadProgressBar from '@/components/atoms/UploadProgressBar';
import {useFileUpload} from '@/hooks/useFileUpload';
import {logger} from '@/helpers/logger';
import {
  buildMediaUploadMetadataPayload,
  buildWebpImageUploadFormData
} from '@/helpers/imageUploadForm';
import {Constants} from '@/utils/constants/commonConstants';
import {FEEDBACK_TYPE} from '@/utils/constants/globalStyle';
import {
  PERFECT_STORE_IMAGE_ASPECT_RATIO,
  PERFECT_STORE_IMAGE_ASPECT_RATIO_LABEL,
  PERFECT_STORE_IMAGE_DIMENSIONS,
  PERFECT_STORE_IMAGE_MAX_SIZE_BYTES,
  PERFECT_STORE_IMAGE_TYPES
} from '@/utils/constants/perfectStoreConstants';

const ASPECT_RATIO_TOLERANCE = 0.05;
const IMAGE_DESCRIPTION =
  'Drag and drop your file here (only .webp, .jpg, .png)';

const extractFile = (files) => {
  if (files instanceof File) return files;
  if (files?.length > 0) return files[0];
  return null;
};

const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({width: img.naturalWidth, height: img.naturalHeight, url});
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

const formatAllowedFormats = (types) =>
  types.map((type) => type.split('/')[1].toUpperCase()).join(', ');

/**
 * Reusable image upload field with format/size/dimension/aspect-ratio validation,
 * preview and delete — used for Perfect Store Banner Image and Rack Display images.
 */
const ImageUploadField = ({
  label,
  tooltipText,
  imageUri,
  disabled,
  onChange,
  onDelete,
  acceptedTypes,
  dimensions,
  aspectRatio,
  aspectRatioLabel,
  maxSizeBytes,
  dataTestId,
  containerClassName = 'max-w-[380px] rounded-lg border border-[color:var(--color-border-default)] p-3'
}) => {
  const {uploadFile, progress} = useFileUpload();
  const [error, setError] = useState('');

  const validateFile = useCallback(
    async (file) => {
      if (!acceptedTypes.includes(file.type)) {
        return `Unsupported format. Allowed formats: ${formatAllowedFormats(acceptedTypes)}`;
      }
      if (file.size > maxSizeBytes) {
        return `Image must be less than or equal to ${Math.round(maxSizeBytes / 1024)} KB.`;
      }
      try {
        const {width, height, url} = await getImageDimensions(file);
        URL.revokeObjectURL(url);
        if (width !== dimensions.width || height !== dimensions.height) {
          return `Image must be exactly ${dimensions.width} x ${dimensions.height} px.`;
        }
        const ratio = width / height;
        if (
          Math.abs(ratio - aspectRatio) >
          aspectRatio * ASPECT_RATIO_TOLERANCE
        ) {
          return `Image must have a ${aspectRatioLabel} aspect ratio.`;
        }
      } catch {
        return 'Unable to read the image. Please try another file.';
      }
      return '';
    },
    [acceptedTypes, maxSizeBytes, dimensions, aspectRatio, aspectRatioLabel]
  );

  const handleUpload = useCallback(
    async (files) => {
      const file = extractFile(files);
      if (!file) return;

      const validationError = await validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      try {
        setError('');
        const {webpFile, metadata} = await buildWebpImageUploadFormData(file);
        // the media/upload endpoint requires the multipart fields 'images' and 'metadata'
        const response = await uploadFile(
          webpFile,
          Constants.Media,
          'image',
          'images',
          {metadata: JSON.stringify(buildMediaUploadMetadataPayload(metadata))}
        );

        // response is either a bare array or {data: [...]}, matching the
        // same media/upload contract used by Wheel of Fortune/Rewards display.
        const list = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const first = list[0];
        const cdnUrl =
          first?.media?.cdnUrl ||
          first?.cdnUrl ||
          first?.url ||
          response?.data?.cdnUrl ||
          response?.data?.url ||
          '';

        if (!cdnUrl) {
          throw new Error(Constants.ErrorForFileUpload);
        }
        onChange({
          imageUrl: cdnUrl,
          imageUri: URL.createObjectURL(file),
          fileName: file.name
        });
      } catch (uploadError) {
        logger.error('Perfect Store image upload failed:', uploadError);
        setError('Image upload failed. Please try again.');
      }
    },
    [uploadFile, validateFile, onChange]
  );

  const handleDelete = useCallback(() => {
    setError('');
    onDelete();
  }, [onDelete]);

  return (
    <div className={containerClassName}>
      <UploadProgressBar
        progress={progress}
        processingLabel='Processing image...'
      />
      <FileUpload
        dataTestId={dataTestId}
        acceptedExtensions={acceptedTypes.join(',')}
        label={label}
        labelTooltip={tooltipText}
        description={IMAGE_DESCRIPTION}
        type='image'
        variant={imageUri ? 'edit' : 'default'}
        // Label already includes "*"; suppress DS's own "*Required" badge to avoid duplication.
        required={false}
        disabled={disabled}
        imageUri={imageUri}
        feedbackType={error ? FEEDBACK_TYPE.ERROR : FEEDBACK_TYPE.INFO}
        helperText={error}
        onUpload={handleUpload}
        onUploadDelete={handleDelete}
      />
    </div>
  );
};

ImageUploadField.propTypes = {
  label: PropTypes.string.isRequired,
  tooltipText: PropTypes.string,
  imageUri: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  acceptedTypes: PropTypes.arrayOf(PropTypes.string),
  containerClassName: PropTypes.string,
  dimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  }),
  aspectRatio: PropTypes.number,
  aspectRatioLabel: PropTypes.string,
  maxSizeBytes: PropTypes.number,
  dataTestId: PropTypes.string
};

ImageUploadField.defaultProps = {
  tooltipText: '',
  imageUri: '',
  required: false,
  disabled: false,
  acceptedTypes: PERFECT_STORE_IMAGE_TYPES,
  dimensions: PERFECT_STORE_IMAGE_DIMENSIONS,
  aspectRatio: PERFECT_STORE_IMAGE_ASPECT_RATIO,
  aspectRatioLabel: PERFECT_STORE_IMAGE_ASPECT_RATIO_LABEL,
  maxSizeBytes: PERFECT_STORE_IMAGE_MAX_SIZE_BYTES,
  dataTestId: ''
};

export default ImageUploadField;
